import React from "react";
import { Box, Typography, TextField } from "@mui/material";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Add from "@mui/icons-material/Add";
import AutoAwesome from "@mui/icons-material/AutoAwesome";
import Close from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import { useMenuBuilder } from "../context/MenuBuilderContext";
import { IFoodItem, ISubCategoriesJson } from "../../../../redux/food/food.types";
import { MENU_BUILDER_TOKENS } from "../designTokens";
import {
  getCategoryItemCount,
  getSubcategoryItemCount,
  makeSelectionKey,
} from "../utils";
import { MuiNumberField } from "../../../../components/formsComponents/MuiNumberField";

const { colors, spacing, borderRadius, typography } = MENU_BUILDER_TOKENS;

export const ItemGrid: React.FC = () => {
  const {
    activeCategory,
    activeSubcategory,
    selectedItems,
    categoryLimits,
    setCategoryLimit,
    selectCategory,
    addItem,
    removeItem,
    mode,
    visibilityEditMode,
    toggleItemVisibility,
    isItemVisible,
  } = useMenuBuilder();

  // Compute items to display based on active category/subcategory
  const displayItems: IFoodItem[] = React.useMemo(() => {
    if (!activeCategory) return [];
    let items: IFoodItem[] = [];
    if (activeSubcategory) {
      items = activeSubcategory.food_items || [];
    } else {
      items = (activeCategory.subcategories || []).flatMap(
        (sc) => sc.food_items || []
      );
      if (activeCategory.food_items && activeCategory.food_items.length > 0) {
        items = [...activeCategory.food_items, ...items];
      }
    }
    return items;
  }, [activeCategory, activeSubcategory]);

  // Determine subcategory sections to render
  const subcategorySections: { subcategory: ISubCategoriesJson; items: IFoodItem[] }[] = React.useMemo(() => {
    if (!activeCategory) return [];
    const subcategories = activeSubcategory
      ? [activeSubcategory]
      : activeCategory.subcategories || [];
    return subcategories
      .map((sc) => {
        let items = sc.food_items || [];
        // In lead mode, filter out hidden items entirely
        if (mode === "lead") {
          items = items.filter((item) => isItemVisible(item.food_item_uuid ?? ""));
        }
        return { subcategory: sc, items };
      })
      .filter((section) => section.items.length > 0);
  }, [activeCategory, activeSubcategory, mode, isItemVisible]);

  // Handle item click — selectionKey is "categoryUuid|subCategoryUuid|itemUuid"
  const handleItemClick = (
    item: IFoodItem,
    selectionKey: string,
    isSelected: boolean,
    sectionSubUuid?: string | null,
  ) => {
    if (isSelected) {
      removeItem(selectionKey);
    } else {
      // Anchor to the exact subcategory section this chip lives in
      addItem(item, sectionSubUuid ?? null);
    }
  };

  // Compute summary counts
  const subcategoryCount = mode === "lead"
    ? subcategorySections.length
    : (activeCategory?.subcategories?.length ?? 0);
  const totalDishCount = mode === "lead"
    ? displayItems.filter((item) => isItemVisible(item.food_item_uuid ?? "")).length
    : displayItems.length;

  // Empty state: no category selected
  if (!activeCategory) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        minHeight="50vh"
        px={2}
      >
        <Typography variant="h6" color="text.secondary" textAlign="center">
          Select a category to view items
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={1} textAlign="center">
          Choose a category from the panel on the left to browse available food items.
        </Typography>
      </Box>
    );
  }

  // Render a single chip item
  const renderChip = (item: IFoodItem, sectionSubUuid?: string) => {
    const itemUuid = item.food_item_uuid ?? "";
    const catUuid = activeCategory?.category_uuid ?? "";
    // Resolve which sub-category this chip belongs to (section context takes priority)
    const resolvedSubUuid =
      sectionSubUuid ?? item.sub_category_uuid ?? "";
    const selectionKey = makeSelectionKey(catUuid, resolvedSubUuid, itemUuid);
    const isSelected = selectedItems.some(
      (i) => (i as any)._selectionKey === selectionKey,
    );
    const isVisible = isItemVisible(itemUuid);

    // Master mode + visibility edit: show eye toggle + allow selection if visible
    if (mode === "master" && visibilityEditMode) {
      return (
        <Box
          key={itemUuid}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 1,
            borderRadius: borderRadius.chip,
            cursor: isVisible ? "pointer" : "default",
            bgcolor: isSelected ? "#E8F0F8" : isVisible ? colors.chipUnselected : "#f1f5f9",
            border: isSelected
              ? `2px solid ${colors.chipSelected}`
              : isVisible
                ? `1.5px solid ${colors.chipUnselectedBorder}`
                : `1.5px dashed #94A3B8`,
            opacity: isVisible ? 1 : 0.6,
            transition: "all 0.15s ease",
            minWidth: 120,
            "&:hover": isVisible ? { transform: "translateY(-1px)" } : { opacity: 1 },
          }}
        >
          {/* Eye toggle — LEFT */}
          <Box
            onClick={(e) => { e.stopPropagation(); toggleItemVisibility(itemUuid); }}
            sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          >
            {isVisible ? (
              <VisibilityIcon sx={{ fontSize: 18, color: colors.chipSelected }} />
            ) : (
              <VisibilityOffIcon sx={{ fontSize: 18, color: colors.textMuted }} />
            )}
          </Box>
          {/* Item name — click to select/deselect (only if visible) */}
          <Typography
            onClick={() => {
              if (isVisible) handleItemClick(item, selectionKey, isSelected, resolvedSubUuid);
            }}
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              whiteSpace: "nowrap",
              flex: 1,
              textDecoration: isVisible ? "none" : "line-through",
              color: isVisible ? colors.textPrimary : colors.textMuted,
              cursor: isVisible ? "pointer" : "default",
            }}
          >
            {item.item_name}
          </Typography>
          {/* Selection indicator (if visible) */}
          {isVisible && isSelected && (
            <CheckCircle
              onClick={() => handleItemClick(item, selectionKey, isSelected, resolvedSubUuid)}
              sx={{ fontSize: 20, color: colors.chipSelected, cursor: "pointer" }}
            />
          )}
          {isVisible && !isSelected && (
            <Box
              onClick={() => handleItemClick(item, selectionKey, isSelected, resolvedSubUuid)}
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                border: `1.5px solid ${colors.textMuted}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <Add sx={{ fontSize: 13, color: colors.textMuted }} />
            </Box>
          )}
        </Box>
      );
    }

    // Check if subcategory limit is exceeded
    const subUuid = resolvedSubUuid || "";
    const subLimit = categoryLimits[subUuid] || 0;
    const subCount = subUuid ? getSubcategoryItemCount(selectedItems, subUuid) : 0;
    const isSubLimitExceeded = subLimit > 0 && subCount > subLimit && isSelected;

    return (
      <Box
        key={selectionKey}
        onClick={() => handleItemClick(item, selectionKey, isSelected, resolvedSubUuid)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: .75,
          borderRadius: borderRadius.chip,
          cursor: "pointer",
          bgcolor: isSubLimitExceeded
            ? "#fef2f2"
            : isSelected
              ? "#E8F0F8"
              : colors.chipUnselected,
          border: isSubLimitExceeded
            ? "2px solid #fca5a5"
            : isSelected
              ? `2px solid ${colors.chipSelected}`
              : `1.5px solid ${colors.chipUnselectedBorder}`,
          boxShadow: isSelected
            ? isSubLimitExceeded
              ? "0 2px 8px rgba(239, 68, 68, 0.15)"
              : "0 2px 8px rgba(13, 59, 102, 0.2)"
            : "none",
          color: colors.textPrimary,
          transition: "all 0.15s ease",
          minWidth: 140,
          "&:hover": {
            boxShadow: isSelected
              ? "0 4px 12px rgba(13, 59, 102, 0.25)"
              : "0 2px 8px rgba(0,0,0,0.06)",
            transform: "translateY(-1px)",
          },
        }}
      >
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {item.item_name}
        </Typography>
        {isSelected ? (
          <CheckCircle sx={{ fontSize: 22, color: isSubLimitExceeded ? "#ef4444" : colors.chipSelected }} />
        ) : (
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              border: `1.5px solid ${colors.textMuted}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Add sx={{ fontSize: 14, color: colors.textMuted }} />
          </Box>
        )}      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        gap: 1,
        p: 1.5,
        bgcolor: colors.contentBg,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1, borderBottom: `1px solid ${colors.divider}` }}>
        {/* Left: Category name + subtitle */}
        <Box>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
            <Typography
              sx={{
                ...typography.heading,
                fontSize: "20px",
                color: colors.textPrimary,
              }}
            >
              {activeSubcategory
                ? activeSubcategory.subcategory_name
                : activeCategory.category_name}
            </Typography>
            <Typography
              sx={{
                ...typography.caption,
                color: colors.textSecondary,
              }}
            >
              {subcategoryCount} subcategories · {totalDishCount} dishes
            </Typography>
          </Box>
          {/* Category limit exceeded warning */}
          {categoryLimits[activeCategory.category_uuid ?? ""] > 0 && (() => {
            const catUuid = activeCategory.category_uuid ?? "";
            const limit = categoryLimits[catUuid];
            const itemCount = getCategoryItemCount(selectedItems, catUuid);
            return itemCount > limit ? (
              <Typography sx={{ fontSize: "11px", color: "#ef4444", fontWeight: 600, mt: 0.25 }}>
                ⚠ Max {limit} items allowed
              </Typography>
            ) : null;
          })()}
        </Box>

        {/* Right: Limit + Shrink button */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Category Limit Control */}
          {mode === "master" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Typography sx={{ ...typography.uppercaseLabel, color: colors.textMuted, fontSize: "10px" }}>
                CATEGORY LIMIT · Max items
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  border: `1.5px solid ${
                    (() => {
                      const catUuid = activeCategory.category_uuid ?? "";
                      const limit = categoryLimits[catUuid];
                      const itemCount = getCategoryItemCount(selectedItems, catUuid);
                      return itemCount > limit ? "#ef4444" : itemCount >= limit ? "#f59e0b" : colors.divider;
                    })()
                  }`,
                  borderRadius: "8px",
                  px: 1,
                  py: 0.25,
                }}
              >
                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: colors.textPrimary, minWidth: "20px", textAlign: "center" }}>
                  {getCategoryItemCount(selectedItems, activeCategory.category_uuid ?? "")}
                </Typography>
                <Typography sx={{ fontSize: "14px", color: colors.textMuted, mx: 0.5 }}>/</Typography>
                {mode === "master" ? (
                  <MuiNumberField
                    name="categoryLimit"
                    value={categoryLimits[activeCategory.category_uuid ?? ""] || ""}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      setCategoryLimit(activeCategory.category_uuid ?? "", isNaN(val) ? 0 : val);
                    }}
                    placeholder="∞"
                    sx={{ width: "40px", "& .MuiOutlinedInput-root": { "& fieldset": { border: "none" } }, "& .MuiInputBase-input": { p: "2px 4px", textAlign: "center", fontSize: "14px", fontWeight: 600 } }}
                  />
                ) : (
                  <Typography sx={{ fontSize: "14px", fontWeight: 600, color: (() => {
                    const catUuid = activeCategory.category_uuid ?? "";
                    const limit = categoryLimits[catUuid];
                    const itemCount = getCategoryItemCount(selectedItems, catUuid);
                    return itemCount > limit ? "#ef4444" : colors.textPrimary;
                  })(), minWidth: "20px", textAlign: "center" }}>
                    {categoryLimits[activeCategory.category_uuid ?? ""]}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          {/* Close button */}
          <Box
            onClick={() => selectCategory(null)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: "50%",
              border: `1px solid ${colors.divider}`,
              bgcolor: colors.panelBg,
              cursor: "pointer",
              "&:hover": { bgcolor: "#F1F5F9" },
            }}
          >
            <Close sx={{ fontSize: 16, color: colors.textSecondary }} />
          </Box>
        </Box>
      </Box>

      {/* Items grouped by subcategory */}
      <Box sx={{ overflowY: "auto", flex: 1 }}>
        {subcategorySections.length === 0 && displayItems.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="40vh">
            <Typography variant="body1" color="text.secondary">
              No items found
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {subcategorySections.map((section) => (
              <Box key={section.subcategory.food_subcategory_name_uuid}>
                {/* Subcategory section wrapper */}
                {(() => {
                  const subUuid = section.subcategory.food_subcategory_name_uuid ?? "";
                  const subLimit = categoryLimits[subUuid] || 0;
                  const subCount = getSubcategoryItemCount(selectedItems, subUuid);
                  const isLimitExceeded = subLimit > 0 && subCount > subLimit;

                  return (
                    <Box
                      sx={{
                        bgcolor: "transparent",
                        transition: "all 0.2s ease",
                      }}
                    >
                {/* Subcategory header */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                      <Typography sx={{ fontWeight: 700, color: colors.textPrimary, fontSize: "16px" }}>
                        {section.subcategory.subcategory_name}
                      </Typography>
                      <Typography sx={{ ...typography.caption, color: colors.textMuted }}>
                        {section.items.length} available
                      </Typography>
                    </Box>
                  </Box>
                  {/* Subcategory limit control — master mode only */}
                  {mode === "master" && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography sx={{ ...typography.uppercaseLabel, color: colors.textMuted, fontSize: "9px" }}>
                        LIMIT
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          border: `1.5px solid ${isLimitExceeded ? "#ef4444" : colors.divider}`,
                          borderRadius: "6px",
                          px: 0.75,
                          py: 0.15,
                        }}
                      >
                        <Typography sx={{ fontSize: "12px", fontWeight: 600, color: colors.textPrimary, minWidth: "16px", textAlign: "center" }}>
                          {subCount}
                        </Typography>
                        <Typography sx={{ fontSize: "12px", color: colors.textMuted, mx: 0.25 }}>/</Typography>
                        <MuiNumberField
                          name={`subLimit_${subUuid}`}
                          value={subLimit || ""}
                          onChange={(e) => {
                            const val = parseInt(e.target.value);
                            setCategoryLimit(subUuid, isNaN(val) ? 0 : val);
                          }}
                          placeholder="∞"
                          sx={{ width: "32px", "& .MuiOutlinedInput-root": { "& fieldset": { border: "none" } }, "& .MuiInputBase-input": { p: "1px 2px", textAlign: "center", fontSize: "12px", fontWeight: 600 } }}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
                {/* Subcategory limit exceeded warning */}
                {isLimitExceeded && (
                  <Typography sx={{ fontSize: "11px", color: "#ef4444", fontWeight: 600, mb: 0.5 }}>
                    ⚠ Max {subLimit} items allowed in {section.subcategory.subcategory_name}
                  </Typography>
                )}
                {/* Chips */}
                <Box sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                }}>
                  {section.items.map((item) => renderChip(item, section.subcategory.food_subcategory_name_uuid ?? undefined))}
                </Box>
                    </Box>
                  );
                })()}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};
